import type { WasteStatsBucket } from "@/hooks/use-waste-stats";
import type { Lens } from "@/hooks/use-lens";
import { useTranslation } from "@/i18n/use-translation";
import { formatCurrency, formatWeight } from "@/lib/format";
import { moneySplit } from "@/lib/money-split";
import { ChartCard } from "@/components/waste/reports/chart-card";
import { cn } from "@/lib/utils";

type Props = {
  /** "Explore this" target for this panel (POS2-886); null when unavailable. */
  exploreHref?: string | null;
  buckets: WasteStatsBucket[];
  lens: Lens;
  currency: string;
};

/**
 * "Where the money goes" (design §7, position 5). Splits the by_waste_type buckets
 * into avoidable / unavoidable / unknown three-way bars with a "{n}% recoverable"
 * header — the number a GM can act on (POS2-947 expanded the earlier two-way split;
 * Unknown = genuinely-blind captures, excluded from the recoverable %). Packaging is
 * excluded from the split (POS2-861); the mapping lives in `lib/money-split`. Bar
 * widths are each bucket's share of the food-waste total in the active lens.
 */
export function WhereMoneyGoes({ exploreHref, buckets, lens, currency }: Props) {
  const { t } = useTranslation();
  const { avoidable, unavoidable, unknown, recoverablePct } = moneySplit(buckets, lens);

  const amount = (a: { cost: number; weight: number }) => (lens === "weight" ? a.weight : a.cost);
  const total = amount(avoidable) + amount(unavoidable) + amount(unknown);
  const share = (a: { cost: number; weight: number }) =>
    total > 0 ? (amount(a) / total) * 100 : 0;

  return (
    <ChartCard
      exploreHref={exploreHref}
      title={t("dashboard.widget.money")}
      description={t("dashboard.moneyDescription")}
      isEmpty={total === 0}
      action={
        recoverablePct == null ? undefined : (
          <span className="text-xs text-gray-500" data-testid="money-recoverable">
            <span className="font-display text-base font-extrabold tracking-tight text-gray-900">
              {Math.round(recoverablePct)}%
            </span>{" "}
            {t("dashboard.moneyRecoverable")}
          </span>
        )
      }
    >
      <div className="flex flex-col gap-4" data-testid="where-money-goes">
        <SplitRow
          name={t("dashboard.moneyAvoidable")}
          examples={t("dashboard.moneyAvoidableExamples")}
          weight={avoidable.weight}
          cost={avoidable.cost}
          lens={lens}
          currency={currency}
          fillPct={share(avoidable)}
          barClass="bg-amber-500"
          testId="money-avoidable"
        />
        <SplitRow
          name={t("dashboard.moneyUnavoidable")}
          examples={t("dashboard.moneyUnavoidableExamples")}
          weight={unavoidable.weight}
          cost={unavoidable.cost}
          lens={lens}
          currency={currency}
          fillPct={share(unavoidable)}
          barClass="bg-slate-300"
          testId="money-unavoidable"
        />
        <SplitRow
          name={t("dashboard.moneyUnknown")}
          examples={t("dashboard.moneyUnknownExamples")}
          weight={unknown.weight}
          cost={unknown.cost}
          lens={lens}
          currency={currency}
          fillPct={share(unknown)}
          barClass="bg-gray-400"
          testId="money-unknown"
        />
      </div>
    </ChartCard>
  );
}

function SplitRow({
  name,
  examples,
  weight,
  cost,
  lens,
  currency,
  fillPct,
  barClass,
  testId,
}: {
  name: string;
  examples: string;
  weight: number;
  cost: number;
  lens: Lens;
  currency: string;
  fillPct: number;
  barClass: string;
  testId: string;
}) {
  const { locale } = useTranslation();
  const main =
    lens === "weight" ? formatWeight(locale, weight) : formatCurrency(locale, cost, currency);
  const secondary = lens === "cost" ? formatWeight(locale, weight) : null;

  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-gray-900">{name}</p>
          <p className="text-xs text-gray-400">{examples}</p>
        </div>
        <div className="shrink-0 text-right">
          <span className="font-display text-lg font-extrabold tracking-tight tabular-nums text-gray-900">
            {main}
          </span>
          {secondary && (
            <span className="ml-1.5 text-xs tabular-nums text-gray-400">{secondary}</span>
          )}
        </div>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
        <span
          className={cn("block h-full rounded-full", barClass)}
          style={{ width: `${fillPct}%` }}
        />
      </div>
    </div>
  );
}
